/**
 * LanguageSwitcher — přepínač jazyků v shellu. Jazyk se ukládá per-user na účet
 * (ui_prefs 'lang') + localStorage jako rychlá cache; načítá se v AppLayout po
 * loginu. Stejný layout jako ThemeSwitcher: ikona vlevo + volby v řadě.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { normalizeLang, type Lang, setLang, SUPPORTED } from '../lib/i18n';
import { api } from '../lib/api';

/** Přepne jazyk lokálně (i18n+localStorage) a uloží ho na účet (fire-and-forget). */
export function persistLang(l: Lang): Promise<void> {
  setLang(l);
  // Bez await — přepnutí jazyka nesmí čekat na síť; bez entity server ignoruje.
  return api.mcpSession('ui.prefs_set', { key: 'lang', value: { lang: l } }).then(() => {});
}

export function LanguageSwitcher() {
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const current = normalizeLang(i18n.language || 'cs');
  const switchLang = (l: Lang) => {
    qc.setQueryData(['ui-lang'], { value: { lang: l } });
    persistLang(l).catch(() => {
      void qc.invalidateQueries({ queryKey: ['ui-lang'] });
    });
  };

  return (
    <div
      className='inline-flex items-center gap-1 text-xs'
      title='Language'
      role='group'
      aria-label='Jazyk'
    >
      <Languages size={13} className='text-gray-500 shrink-0' />
      {SUPPORTED.map((l) => {
        const active = current === l;
        return (
          <button
            key={l}
            type='button'
            onClick={() => switchLang(l)}
            aria-pressed={active}
            className={`px-1.5 py-0.5 rounded uppercase tracking-wide transition-colors ${
              active
                ? 'bg-gray-700 text-gray-100'
                : 'text-gray-500 hover:text-gray-200'
            }`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}
